import { hasActionCable, isAdmin, isConsumer, isKds, isKiosk, isOrderCollectionDisplay, isPos } from 'src/config'
import Model from 'src/models/model'
import DynamicAuthLayout from 'src/modules/auth/layouts/DynamicAuthLayout'
import { buildAuthChildRoutes } from 'src/modules/auth/routes'
import { DEFAULT_SHARED_DOMAIN_SITE_GUARDS } from 'src/modules/consumer/guards/sharedDomainSiteSwitching'
import { prependWithConsumerRoot } from 'src/router/utils'

function isArrayOrString (variable) {
  if (typeof variable === typeof [] || typeof variable === typeof '') {
    return true
  }
  return false
}

export default async ({ app, router }) => {
  await Model.configuration.ensureLoaded()
  const defaultSite = Model.configuration.currentSite
  const isHostnameSharedAcrossSites = defaultSite.isHostnameSharedAcrossSites
  const sharedDomainSitesByPath = defaultSite.sharedHostnameSiteIdsByPath

  let setHeaders = false
  function initialHeaderSetup () {
    setHeaders = true

    if (!isAdmin) return

    if (!Model.context.currentUser) return

    app.$updateGlobalHeaders({
      'site-id': Model.context.currentUser.siteId,
      'circuit-id': Model.context.currentUser.circuitId
    })
  }

  async function checkAuthentication (to, _from, next) {
    if (!setHeaders) {
      await Model.ensureBooted()
      initialHeaderSetup()
    }

    const matchedRouteWithAuthRequirement = to.matched.find(route => route.meta.auth)
    if (matchedRouteWithAuthRequirement) {
      await Model.ensureBooted()

      if (matchedRouteWithAuthRequirement.meta.auth.loggedOut) {
        // Route only accessible if logged out
        if (Model.context.currentUser) {
          next('/')
        } else {
          next()
        }
      } else if (!Model.context.currentUser) {
        next({ name: 'auth:login' })
      } else if (matchedRouteWithAuthRequirement.meta.auth.admin && !Model.context.currentUser.admin) {
        next({ name: 'auth:unauthorized' })
      } else if (
        isArrayOrString(matchedRouteWithAuthRequirement.meta.auth) &&
        _.difference(matchedRouteWithAuthRequirement.meta.auth, Model.context.currentUser.roles).length !== 0
      ) {
        next({ name: 'auth:account' })
      } else {
        next()
      }
    } else {
      next()
    }
  }

  /**
   * Set global route guards
   */
  router.beforeEach(checkAuthentication)

  /**
   * Set authentication routes
   */

  // Helper function to build same set of consumer routes multiple times, for the default
  // site and for any other sites that share the same hostname
  function buildNamespacedRootRoute (namespace, siteId, siteRootPath) {
    const isForDefaultRootPath = namespace === 'auth'

    // The base '/auth' paths aren't really used but we need a distinct path for the root route
    // to avoid conflicts with other root routes. To accommodate this, all child routes are added
    // as absolute root paths instead of relative subpaths. See `buildAuthChildRoutes()`.
    const rootRoute = {
      name: `${namespace}:root`,
      path: isForDefaultRootPath ? '/auth' : prependWithConsumerRoot(`${siteRootPath}/auth`),
      component: DynamicAuthLayout,
      meta: { layout: 'PlainLayout' },
      children: buildAuthChildRoutes(namespace, isForDefaultRootPath ? '/' : prependWithConsumerRoot(siteRootPath))
    }

    if (isConsumer && isHostnameSharedAcrossSites) {
      // `meta.requiredSiteId` is used by the `ensureSwitchedToSiteRequiredByRoute` guard to determine which site to switch to
      // when the user navigates to this route and its child routes.
      rootRoute.meta.requiredSiteId = siteId

      _.each(rootRoute.children, route => {
        route.beforeEnter = _([
          DEFAULT_SHARED_DOMAIN_SITE_GUARDS,
          route.beforeEnter
        ]).flattenDeep().compact().value()
      })
    }

    return rootRoute
  }

  router.addRoute(buildNamespacedRootRoute('auth', defaultSite.id, '/'))

  if (isConsumer && isHostnameSharedAcrossSites) {
    _.each(sharedDomainSitesByPath, (siteId, siteRootPath) => {
      router.addRoute(buildNamespacedRootRoute(`auth:site-${siteId}`, siteId, siteRootPath))
    })
  }

  let postAuthRoute
  let authData = {}
  const helper = {}
  helper.setPostAuthRoute = (path) => { postAuthRoute = path }
  helper.getPostAuthRoute = () => postAuthRoute
  helper.navigatePostAuth = (fallback = '/') => {
    router.push(postAuthRoute || fallback)
    postAuthRoute = null
  }
  helper.setAuthData = (data) => { authData = data }
  helper.getAuthData = () => authData

  async function logout () {
    await Model.context.currentUser.logOut()

    await Model.context.fetch(true)
    if (!isPos) {
      await Model.configuration.validate()
    }

    if (isAdmin) {
      app.$updateGlobalHeaders({
        'site-id': null,
        'circuit-id': null
      })
    }

    this.$router.push('/').catch(() => {})

    if (isAdmin) {
      app.config.globalProperties.$handleAuthChangeForTracking && app.config.globalProperties.$handleAuthChangeForTracking()
    }
  }

  async function handleAuthChange (circuitId = null, siteId = null, forceUpdateHeaders = false) {
    if (isAdmin || forceUpdateHeaders) {
      app.$updateGlobalHeaders({
        'site-id': siteId,
        'circuit-id': circuitId
      }, true)
    }

    if (hasActionCable) {
      await Model.cable.connection.reopen()
    }

    await Model.refetchAllBaseQueries()
    app.config.globalProperties.$handleAuthChangeForTracking?.()

    if (Model.configuration.currentSite) {
      localStorage.splashImageUrl = Model.configuration.currentSite.splashImageUrl
      localStorage.splashBackgroundColor = Model.configuration.currentSite.splashBackgroundColor
    }

    updateCableSubscriptions()
  }

  function unsubscribeFromCableSubscriptions () {
    if (hasActionCable) {
      Model.configuration.unsubscribeFromRemoteModelChanges()
      app.$remoteCommands.unsubscribe()
      app.$modelFormLock.unsubscribe()
      app.config.globalProperties.$salesRestrictions.unsubscribe()
    }
  }

  async function updateCableSubscriptions () {
    if (hasActionCable) {
      await Model.context.ensureLoaded()
      const connectionScopeEstablished = ((isPos || isKiosk) && !_.isNil(Model.context.currentRegisterClient?.registerStationId)) ||
        (isKds && !_.isNil(Model.context.currentKdsClient?.kitchenDisplayId)) ||
        (isOrderCollectionDisplay && !_.isNil(Model.context.currentOrderCollectionDisplayClient?.orderCollectionDisplayTypeId)) ||
        (!isPos && !isKiosk && !isKds && !_.isNil(Model.context.currentUser))

      if (connectionScopeEstablished) {
        Model.configuration.subscribeToRemoteModelChanges()
        app.$remoteCommands.subscribe()
        app.$modelFormLock.subscribe()
        app.config.globalProperties.$salesRestrictions.subscribe()
      }
    }
  }
  helper.updateCableSubscriptions = updateCableSubscriptions

  // This is used for changing between sites within a circuit for websites that host multiple sites of a circuit
  async function handleSiteChange () {
    Model.configuration.validate()
  }

  app.config.globalProperties.$auth = helper
  app.$auth = helper

  app.config.globalProperties.$handleAuthChange = handleAuthChange
  app.$handleAuthChange = handleAuthChange

  app.config.globalProperties.$unsubscribeFromCableSubscriptions = unsubscribeFromCableSubscriptions
  app.$unsubscribeFromCableSubscriptions = unsubscribeFromCableSubscriptions

  app.config.globalProperties.$handleSiteChange = handleSiteChange
  app.$handleSiteChange = handleSiteChange

  app.config.globalProperties.$logout = logout
  app.$logout = logout
}
